import { useId } from "react";
import type { KeyboardEvent } from "react";
import { clamp, cx } from "./utils";

export interface SliderProps {
  /** Current value, or `null` for "not judged yet" (tri-state). */
  value: number | null;
  /** Called with the new value on drag/keyboard change. */
  onChange: (value: number) => void;
  /** @default 0 */
  min?: number;
  /** @default 1 */
  max?: number;
  /** @default 0.05 */
  step?: number;
  /** Accessible name (falls back to `aria-labelledby` from a `Field`). */
  label?: string;
  /** Override the announced value text; defaults to a 2-decimal number. */
  formatValue?: (v: number) => string;
  disabled?: boolean;
  /**
   * Renders the "masked / undecidable" look (arbitration split, §14.4).
   * Implies disabled and announces the value as unset.
   */
  masked?: boolean;
  /** Hide the floating value bubble. */
  hideBubble?: boolean;
  className?: string;
  id?: string;
}

const fmt = (v: number) => v.toFixed(2);

/**
 * Token-styled range slider with a value bubble and full keyboard support
 * (arrows = ±step, PageUp/Down = ±5 steps, Home/End = min/max).
 */
export function Slider({
  value,
  onChange,
  min = 0,
  max = 1,
  step = 0.05,
  label,
  formatValue = fmt,
  disabled = false,
  masked = false,
  hideBubble = false,
  className,
  id,
}: SliderProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  const isUnset = value == null || masked;
  const shown = clamp(value ?? (min + max) / 2, min, max);
  const pct = max === min ? 0 : ((shown - min) / (max - min)) * 100;
  const off = disabled || masked;

  const bump = (delta: number) => {
    const next = clamp(Number(((value ?? min) + delta).toFixed(6)), min, max);
    onChange(next);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (off) return;
    switch (e.key) {
      case "ArrowLeft":
      case "ArrowDown":
        e.preventDefault();
        bump(-step);
        break;
      case "ArrowRight":
      case "ArrowUp":
        e.preventDefault();
        bump(step);
        break;
      case "PageDown":
        e.preventDefault();
        bump(-step * 5);
        break;
      case "PageUp":
        e.preventDefault();
        bump(step * 5);
        break;
      case "Home":
        e.preventDefault();
        onChange(min);
        break;
      case "End":
        e.preventDefault();
        onChange(max);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={cx(
        "xz-slider",
        off && "is-disabled",
        masked && "is-masked",
        isUnset && "is-unset",
        className,
      )}
      style={{ ["--xz-slider-pct" as string]: `${pct}%` }}
    >
      <input
        id={inputId}
        className="xz-slider__input"
        type="range"
        min={min}
        max={max}
        step={step}
        value={shown}
        disabled={off}
        aria-label={label}
        aria-valuenow={isUnset ? undefined : shown}
        aria-valuetext={masked ? "已掩码" : isUnset ? "未评定" : formatValue(shown)}
        onKeyDown={onKeyDown}
        onChange={(e) => !off && onChange(Number(e.target.value))}
      />
      {!hideBubble && (
        <output className="xz-slider__bubble" htmlFor={inputId} aria-hidden="true">
          {masked ? "掩码" : isUnset ? "—" : formatValue(shown)}
        </output>
      )}
    </div>
  );
}
